'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Building2, X, ArrowRight } from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { onboardingApi } from '@/features/onboarding/services/onboarding-api';

const DISMISS_KEY = 'sf-onboarding-banner-dismissed';
const TOTAL_STEPS = 6;

/**
 * Persistent banner shown in the dashboard when onboarding is incomplete.
 * Dismissible per session — reappears on reload / new tab.
 */
export function OnboardingBanner() {
  const company = useAuthStore((s) => s.company);
  const [dismissed, setDismissed] = useState(false);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [mounted, setMounted] = useState(false);

  // Check sessionStorage once on mount
  useEffect(() => {
    setMounted(true);
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === '1') {
        setDismissed(true);
      }
    } catch {
      // sessionStorage not available (SSR / privacy mode) — banner shows
    }
  }, []);

  // Fetch onboarding step progress
  useEffect(() => {
    if (company?.onboardingCompleted) return;
    onboardingApi
      .getStatus()
      .then((data) => setCurrentStep(data.currentStep || 1))
      .catch(() => {
        // Silent fail — banner still shows with default step
      });
  }, [company?.onboardingCompleted]);

  // Don't render while checking sessionStorage (avoids hydration mismatch)
  if (!mounted) return null;
  // Don't render if onboarding is complete
  if (company?.onboardingCompleted) return null;
  // Don't render if dismissed this session
  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // noop
    }
  };

  const progressPercent = Math.max(0, Math.min(100, ((currentStep - 1) / TOTAL_STEPS) * 100));

  return (
    <div
      className="mb-4 sm:mb-6 rounded-xl border border-amber-200 dark:border-amber-800/60 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 dark:from-amber-950/40 dark:via-orange-950/30 dark:to-amber-950/40 shadow-sm overflow-hidden animate-slide-down"
      role="alert"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5">
        {/* Icon + text */}
        <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 dark:bg-amber-900/50 border border-amber-200 dark:border-amber-800">
            <Building2 className="h-5 w-5 text-amber-700 dark:text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200 leading-tight">
              Completează datele firmei pentru a emite facturi
            </p>
            <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-300/80 leading-tight">
              Pasul {currentStep} din {TOTAL_STEPS} • Durează mai puțin de 5 minute
            </p>
            {/* Progress bar */}
            <div className="mt-2 h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-amber-200/60 dark:bg-amber-900/50">
              <div
                className="h-full rounded-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        </div>

        {/* CTA + dismiss */}
        <div className="flex items-center gap-2 shrink-0">
          <Link
            href="/onboarding"
            className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 dark:bg-amber-700 dark:hover:bg-amber-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm transition-colors whitespace-nowrap"
          >
            Continuă configurarea
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <button
            type="button"
            onClick={handleDismiss}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors"
            aria-label="Ascunde bannerul"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
